import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import type { AlineSelection } from '@aline/common';
import { AlineTransforms, EditingMode, PermissionRole } from '@aline/common';
import { keys } from 'ramda';
import type { Editor } from 'slate';
import { useDocumentParams } from 'src/helpers';
import { useGetEditorUserState } from 'src/hooks/editor-user-state';
import { useMostPermissiveUserStorableRole } from 'src/hooks/storable';
import { updateSlateSelectors } from 'src/layout-engine/hooks';
import { EDITOR_TO_UPDATE_CONTEXT } from 'src/layout-engine/utils';
import { pushNotification } from 'src/notifications';
import { Sentry } from 'src/sentry';
import { useEditingMode } from 'src/store/document/selectors';
import { v4 as uuid4 } from 'uuid';

import {
  deleteAuthorizedTransformsForEditor,
  storeAuthorizedTransformsForEditor,
} from './authorized-transforms';
import type { WrappedTransforms } from './authorized-transforms';

type TransformName = keyof typeof AlineTransforms;
type AlineTransformsType = typeof AlineTransforms;
type AuthorizationDetails = {
  minPermittedRole: PermissionRole;
  permittedEditingModes: EditingMode[];
};
type TransformToAuthorizationDetails = Record<TransformName, AuthorizationDetails>;

// EditingMode helper vars.
const allEditingModes = Object.values(EditingMode);
const editingOnlyModes = [EditingMode.EDITING];
const suggestionPlusEditingModes = [EditingMode.EDITING, EditingMode.SUGGESTING];
const commentPlusEditingModes = [
  EditingMode.EDITING,
  EditingMode.SUGGESTING,
  EditingMode.COMMENTING,
];

// AuthorizationDetails helper vars.
const editorPlusAuthDetails: AuthorizationDetails = {
  minPermittedRole: PermissionRole.Editor,
  permittedEditingModes: editingOnlyModes,
};
const commenterPlusAuthDetails: AuthorizationDetails = {
  minPermittedRole: PermissionRole.Commenter,
  permittedEditingModes: commentPlusEditingModes,
};
const viewerPlusAuthDetails: AuthorizationDetails = {
  minPermittedRole: PermissionRole.Viewer,
  permittedEditingModes: allEditingModes,
};

// Map of transform name -> authorization details.
const transformToAuthDetails: Partial<TransformToAuthorizationDetails> = {
  collapseSel: viewerPlusAuthDetails,
  deleteComment: commenterPlusAuthDetails,
  deleteComments: commenterPlusAuthDetails,
  handleTableSelection: viewerPlusAuthDetails,
  insertComment: commenterPlusAuthDetails,
  moveSel: viewerPlusAuthDetails,
  moveSelHorizontally: viewerPlusAuthDetails,
  moveSelToLineEdge: viewerPlusAuthDetails,
  moveSelToParagraphEdge: viewerPlusAuthDetails,
  nudgeRangeToEdges: viewerPlusAuthDetails,
  select: viewerPlusAuthDetails,
  selectDocPartEdge: viewerPlusAuthDetails,
  setSelection: viewerPlusAuthDetails,
  // Viewer so the assignee can sign regardless of permission role.
  signOrClearSignatureField: viewerPlusAuthDetails,
  // Viewer so the assignee can fill out regardless of permission role.
  switchAllDCsWithFieldId: viewerPlusAuthDetails,
  // Viewer so the assignee can fill out regardless of permission role.
  switchDynamicContentTrigger: viewerPlusAuthDetails,
  // Viewer so the assignee can fill out regardless of permission role.
  toggleCheckbox: viewerPlusAuthDetails,
  unhangRange: viewerPlusAuthDetails,
  // Viewer so the assignee can fill out regardless of permission role.
  updateInputField: viewerPlusAuthDetails,
  upsertDynamicContentDefault: editorPlusAuthDetails,
};

const allowedSigningCeremonyTransforms: TransformName[] = [
  'updateInputField',
  'signOrClearSignatureField',
  'switchAllDCsWithFieldId',
  'toggleCheckbox',
];
const permittedOnlyDuringSigning: TransformName[] = ['signOrClearSignatureField'];

const roleToPermittedEditingModes: Record<PermissionRole, EditingMode[]> = {
  [PermissionRole.Owner]: allEditingModes,
  [PermissionRole.Editor]: allEditingModes,
  [PermissionRole.Suggester]: [EditingMode.SUGGESTING, EditingMode.COMMENTING, EditingMode.VIEWING],
  [PermissionRole.Commenter]: [EditingMode.COMMENTING, EditingMode.VIEWING],
  [PermissionRole.Viewer]: [EditingMode.VIEWING],
};

export function useEditorUpdateState(editor: Editor) {
  const [selection, setSelection] = useState<AlineSelection | null>(null);
  const [editorOnChangeCounter, setEditorOnChangeCounter] = useState(0);
  const pendingCheckboxOptionLabelKeysRef = useRef(new Set<string>());

  EDITOR_TO_UPDATE_CONTEXT.set(editor, {
    pendingCheckboxOptionLabelKeys: pendingCheckboxOptionLabelKeysRef.current,
    setEditorOnChangeCounter,
    setSelection,
  });

  /**
   * Update any selectors created with useSlateSelector. We do this inside useLayoutEffect and
   * queueMicrotask so that if any selector functions depend on outdated content model or render
   * model paths, the render functions that provide these paths have a chance to re-render and
   * supply new paths before the selector is re-evaluated.
   */
  const prevEditorOnChangeCounter = useRef(editorOnChangeCounter);
  useLayoutEffect(() => {
    if (prevEditorOnChangeCounter.current !== editorOnChangeCounter) {
      queueMicrotask(() => updateSlateSelectors(editor));
      prevEditorOnChangeCounter.current = editorOnChangeCounter;
    }
  });

  return { editorOnChangeCounter, selection };
}

/**
 * Hook that sets editor-scoped authorized transforms.
 */
export function useCreateWrappedTransforms(editor: Editor): WrappedTransforms {
  const { editingMode } = useEditingMode();
  const { documentId } = useDocumentParams();
  const { isInSigningCeremony } = useGetEditorUserState();
  const { role } = useMostPermissiveUserStorableRole(documentId);

  const wrappedTransforms = useMemo(
    () =>
      wrapTransformsWithAuthorization(AlineTransforms, {
        editingMode,
        isInSigningCeremony,
        role,
      }),
    [editingMode, isInSigningCeremony, role]
  );

  useEffect(() => {
    storeAuthorizedTransformsForEditor(editor, wrappedTransforms);

    return () => deleteAuthorizedTransformsForEditor(editor);
  }, [editor, wrappedTransforms]);

  return wrappedTransforms;
}

export enum UnauthorizedTransformMsg {
  ChangeNotPermittedDuringSigning = 'Change is not permitted during signing.',
  ChangeNotPermittedInEditingMode = 'Change is not permitted in current editing mode.',
  ChangeOnlyPermittedInSigning = 'Change is only permitted during signing.',
  NotAuthorizedEditingMode = 'Change invalid.  You are in an unauthorized editing mode.',
  UserLacksRole = 'You lack the role necessary to make the requested change.',
}

export function getIsUnauthorizedTransformMsg(msg: string): msg is UnauthorizedTransformMsg {
  return Object.values(UnauthorizedTransformMsg).some(
    unauthorizedTransformMsg => unauthorizedTransformMsg === msg
  );
}

/**
 * Wrap AlineTransforms with authorization and proper error handling.
 */
export function wrapTransformsWithAuthorization(
  nonAuthorizedTransforms: AlineTransformsType,
  options: {
    editingMode: EditingMode | null;
    isInSigningCeremony: boolean;
    role: PermissionRole | null;
  }
): WrappedTransforms {
  const { editingMode, isInSigningCeremony, role } = options;
  const allowedUserEditingModes = role ? roleToPermittedEditingModes[role] : [];

  return keys(nonAuthorizedTransforms).reduce<WrappedTransforms>((acc, transformName) => {
    const transform = acc[transformName];

    return {
      // eslint-disable-next-line aline/no-performance-antipatterns
      ...acc,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      [transformName]: (...args: any[]) => {
        try {
          const {
            minPermittedRole = PermissionRole.Suggester,
            permittedEditingModes = suggestionPlusEditingModes,
          } = transformToAuthDetails[transformName] ?? {};
          const userLacksRequiredRole = !hasRequiredRole(minPermittedRole, role);
          if (userLacksRequiredRole) throw new Error(UnauthorizedTransformMsg.UserLacksRole);

          const userIsInInvalidEditingMode =
            editingMode && !allowedUserEditingModes.includes(editingMode);
          if (userIsInInvalidEditingMode)
            throw new Error(UnauthorizedTransformMsg.NotAuthorizedEditingMode);

          const transformDoesNotPermitEditingMode =
            editingMode && !permittedEditingModes.includes(editingMode);
          if (transformDoesNotPermitEditingMode)
            throw new Error(UnauthorizedTransformMsg.ChangeNotPermittedInEditingMode);

          const isNotPermittedInSigningCeremony =
            isInSigningCeremony && !allowedSigningCeremonyTransforms.includes(transformName);
          if (isNotPermittedInSigningCeremony)
            throw new Error(UnauthorizedTransformMsg.ChangeNotPermittedDuringSigning);

          const isNotPermittedOutsideSigningCeremony =
            !isInSigningCeremony && permittedOnlyDuringSigning.includes(transformName);
          if (isNotPermittedOutsideSigningCeremony)
            throw new Error(UnauthorizedTransformMsg.ChangeOnlyPermittedInSigning);

          return transform(...args);
        } catch (e) {
          console.error(e);
          Sentry.captureException(e);
          pushNotification({
            key: uuid4(),
            message: getIsUnauthorizedTransformMsg(e.message)
              ? e.message
              : 'An editing error occurred. Refresh the page or contact support.',
            variant: 'error',
          });
        }
      },
    };
  }, nonAuthorizedTransforms);
}

const rolesAscending: PermissionRole[] = [
  PermissionRole.Viewer,
  PermissionRole.Commenter,
  PermissionRole.Suggester,
  PermissionRole.Editor,
  PermissionRole.Owner,
];

function hasRequiredRole(
  minRequiredRole: PermissionRole,
  userRole: PermissionRole | null
): boolean {
  if (!userRole) return false;

  const indexOfMinRole = rolesAscending.indexOf(minRequiredRole);
  const indexOfUserRole = rolesAscending.indexOf(userRole);

  return indexOfUserRole >= indexOfMinRole;
}
